import { useEffect, useMemo, useRef } from 'react';
import { UseDataFetchingProps, ApiResponse } from '../types/index';
import { CACHE_DURATION, MAX_CACHE_ENTRIES, FETCH_DEBOUNCE_DELAY } from '../constants/index';
import buildResultEndpoint from '../../../utils/buildResultEndpoint';
import fetchEndpoint from '../../../services/fetchEndpoint';
import { useSummaryMatchContext } from '@/modules/search/searchGrid/context/SummaryMatchContext';

function buildSummaryMatchErrorMessage(geneCount: number): string {
  return `Could not load the Summary match for ${geneCount} associations from filtered Evidence. The server may have rejected the request because there are too many. Narrow your Evidence filters and run the match again.`;
}

function resolvePagedRows(
  data: unknown,
  paging: ApiResponse['paging'] | undefined,
  pageNum: number,
  pageSize: number,
): { rows: any[]; totalCount: number } {
  const rows = Array.isArray(data) ? data : [];
  const rawTotal = paging?.totalElements;
  const totalElements = rawTotal != null ? Number(rawTotal) : NaN;
  const hasServerPaging = Number.isFinite(totalElements) && totalElements >= 0;

  if (hasServerPaging) {
    const pageRows = rows.length > pageSize ? rows.slice(0, pageSize) : rows;
    return { rows: pageRows, totalCount: totalElements };
  }

  const totalCount = rows.length;
  const start = Math.max(0, pageNum) * pageSize;
  return { rows: rows.slice(start, start + pageSize), totalCount };
}

export const useDataFetching = ({
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  skip,
  limit,
  orderByParam,
  order,
  defaultFilterValue,
  sourceWeights,
  userContext,
  mappingContent,
  checkIncompleteFilters,
  setRowData,
  setTotalCount,
  setTotalResults,
  setLoading,
  setIsSessionExpired,
  setShowPopup,
  setIncompleteFilterMessage,
  cacheRef,
  prevDataRef,
  fetchTimeoutRef,
  enabled = true,
  summaryMatchEncodedParams = null,
  summaryMatchKey = null,
  summaryMatchGeneCount = 0,
  onSummaryMatchError,
}: UseDataFetchingProps) => {
  const { isAuthenticated, authorization, setAuthError, restrictGridResultCount, restrictBrowseAll, groups } =
    userContext;
  const { setResultTotalCount } = useSummaryMatchContext();

  const isTrial = groups?.includes('ROLE_TRIAL');

  const filterValuesKey = useMemo(
    () =>
      JSON.stringify({
        filters: defaultFilterValue
          .filter(f => f.value || f.emptiness)
          .map(f => ({ name: f.name, value: f.value, operator: f.operator, emptiness: f.emptiness })),
        summaryMatchKey,
      }),
    [defaultFilterValue, summaryMatchKey],
  );

  const fetchRequestIdRef = useRef(0);

  const paramsRef = useRef({
    view,
    source,
    tab,
    searchItems,
    skip,
    limit,
    orderByParam,
    order,
    defaultFilterValue,
    sourceWeights,
    isEvidence,
    isMapping,
    mappingContent,
    isAuthenticated,
    authorization,
    restrictGridResultCount,
    restrictBrowseAll,
    isTrial,
    checkIncompleteFilters,
    setRowData,
    setTotalCount,
    setTotalResults,
    setLoading,
    setIsSessionExpired,
    setShowPopup,
    setIncompleteFilterMessage,
    cacheRef,
    prevDataRef,
    summaryMatchEncodedParams,
    summaryMatchGeneCount,
    onSummaryMatchError,
    setResultTotalCount,
  });
  paramsRef.current = {
    view,
    source,
    tab,
    searchItems,
    skip,
    limit,
    orderByParam,
    order,
    defaultFilterValue,
    sourceWeights,
    isEvidence,
    isMapping,
    mappingContent,
    isAuthenticated,
    authorization,
    restrictGridResultCount,
    restrictBrowseAll,
    isTrial,
    checkIncompleteFilters,
    setRowData,
    setTotalCount,
    setTotalResults,
    setLoading,
    setIsSessionExpired,
    setShowPopup,
    setIncompleteFilterMessage,
    cacheRef,
    prevDataRef,
    summaryMatchEncodedParams,
    summaryMatchGeneCount,
    onSummaryMatchError,
    setResultTotalCount,
  };

  useEffect(() => {
    if (!enabled) {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      setLoading(false);
      return;
    }

    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }

    fetchTimeoutRef.current = setTimeout(() => {
      const requestId = ++fetchRequestIdRef.current;
      const p = paramsRef.current;
      if (!(p.view && p.source && p.tab && p.searchItems)) {
        p.setLoading(false);
        return;
      }

      const incompleteMessage = p.checkIncompleteFilters(p.defaultFilterValue);
      p.setIncompleteFilterMessage(incompleteMessage);

      if (incompleteMessage) {
        p.setRowData([]);
        p.setTotalCount(0);
        p.setTotalResults(0);
        p.prevDataRef.current = { data: [], count: 0 };
        p.setLoading(false);
        return;
      }

      const pageNum =
        p.restrictGridResultCount || (p.restrictBrowseAll && p.view === 'ALL') ? 0 : p.skip / p.limit;

      const builtEndpoint = buildResultEndpoint({
        view: p.view,
        source: p.source,
        tab: p.tab,
        searchItems: p.searchItems,
        isEvidence: p.isEvidence,
        isMapping: p.isMapping,
        mappingContent: p.mappingContent,
        pageNum,
        pageSize: p.limit,
        orderByParam: p.orderByParam,
        order: p.order,
        defaultFilterValue: p.defaultFilterValue as any,
        sourceWeights: p.sourceWeights,
        summaryMatchParams: null,
        summaryMatchEncodedParams: !p.isEvidence ? p.summaryMatchEncodedParams : null,
      });

      let urlEndpoint = builtEndpoint?.url ?? null;
      const requestMethod = builtEndpoint?.method ?? 'GET';
      const requestBody = builtEndpoint?.body ?? null;

      if (!p.isAuthenticated && p.skip !== 0) {
        p.setShowPopup(true);
        p.setLoading(false);
        return;
      }

      if (p.restrictBrowseAll && p.view === 'ALL' && p.skip !== 0) {
        p.setShowPopup(true);
        p.setLoading(false);
        return;
      }

      if (p.isTrial && p.skip !== 0) {
        p.setShowPopup(true);
        p.setLoading(false);
        return;
      }

      if (!urlEndpoint) {
        p.setLoading(false);
        return;
      }
      urlEndpoint = !p.isAuthenticated ? `${urlEndpoint}&skey=unauth` : urlEndpoint;

      const cacheKey =
        requestMethod === 'POST'
          ? `${urlEndpoint}|${filterValuesKey}|page=${pageNum}|size=${p.limit}|orderBy=${p.orderByParam ?? ''}|order=${p.order ?? ''}`
          : urlEndpoint;
      if (p.cacheRef.current[cacheKey]) {
        const cached = p.cacheRef.current[cacheKey];
        const isRecentCache = Date.now() - cached.timestamp < CACHE_DURATION;

        if (isRecentCache) {
          if (requestId !== fetchRequestIdRef.current) return;
          p.setRowData(cached.data);
          p.setTotalCount(cached.totalCount);
          p.setTotalResults(cached.totalCount);
          p.setIsSessionExpired(false);
          p.prevDataRef.current = { data: cached.data, count: cached.totalCount };
          p.setLoading(false);
          return;
        } else {
          delete p.cacheRef.current[cacheKey];
        }
      }

      p.setLoading(true);
      (fetchEndpoint as any)(
        urlEndpoint,
        p.authorization,
        setAuthError,
        requestMethod === 'POST' ? { method: 'POST', body: requestBody } : null,
      )
        .then((res: ApiResponse) => {
          if (requestId !== fetchRequestIdRef.current) return;
          const data = res?.payload;
          const p2 = paramsRef.current;
          const pageNum =
            p2.restrictGridResultCount || (p2.restrictBrowseAll && p2.view === 'ALL')
              ? 0
              : p2.skip / p2.limit;

          if (data) {
            let { rows, totalCount: totalCountValue } = resolvePagedRows(
              data,
              res?.paging,
              pageNum,
              p2.limit,
            );

            const filteredCount =
              res?.paging?.filteredElements || res?.paging?.filteredCount || res?.filteredCount;
            if (
              filteredCount != null &&
              Number.isFinite(Number(filteredCount)) &&
              Number(filteredCount) >= 0 &&
              Number(filteredCount) < totalCountValue
            ) {
              totalCountValue = Number(filteredCount);
            }

            p2.setRowData(rows);
            p2.setTotalCount(totalCountValue);
            p2.setTotalResults(totalCountValue);
            p2.setIsSessionExpired(false);
            if (p2.summaryMatchEncodedParams) {
              p2.onSummaryMatchError?.(null);
              p2.setResultTotalCount(totalCountValue);
            }

            p2.prevDataRef.current = { data: rows, count: totalCountValue };

            p2.cacheRef.current[cacheKey] = {
              data: rows,
              totalCount: totalCountValue,
              timestamp: Date.now(),
            };

            const cacheKeys = Object.keys(p2.cacheRef.current);
            if (cacheKeys.length > MAX_CACHE_ENTRIES) {
              const sortedKeys = cacheKeys.sort(
                (a, b) => p2.cacheRef.current[a].timestamp - p2.cacheRef.current[b].timestamp,
              );
              sortedKeys.slice(0, cacheKeys.length - MAX_CACHE_ENTRIES).forEach(key => {
                delete p2.cacheRef.current[key];
              });
            }
          } else if (res?.status === 403) {
            p2.setIsSessionExpired(true);
            p2.setRowData([]);
            p2.setTotalCount(0);
            p2.prevDataRef.current = { data: [], count: 0 };
          } else {
            p2.setRowData([]);
            p2.setTotalCount(0);
            p2.prevDataRef.current = { data: [], count: 0 };
            if (p2.summaryMatchEncodedParams) {
              p2.onSummaryMatchError?.(buildSummaryMatchErrorMessage(p2.summaryMatchGeneCount));
            }
          }
        })
        .catch(() => {
          if (requestId !== fetchRequestIdRef.current) return;
          const p2 = paramsRef.current;
          p2.setRowData([]);
          p2.setTotalCount(0);
          if (p2.summaryMatchEncodedParams) {
            p2.onSummaryMatchError?.(buildSummaryMatchErrorMessage(p2.summaryMatchGeneCount));
          }
        })
        .finally(() => {
          if (requestId === fetchRequestIdRef.current) {
            paramsRef.current.setLoading(false);
          }
        });
    }, FETCH_DEBOUNCE_DELAY);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      fetchRequestIdRef.current += 1;
    };
  }, [
    view,
    source,
    tab,
    searchItems,
    skip,
    limit,
    order,
    orderByParam,
    filterValuesKey,
    sourceWeights,
    isEvidence,
    isMapping,
    mappingContent,
    enabled,
  ]);

  return {
    filterValuesKey,
  };
};
